import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Gates a route based on the current user's role + page permissions.
 *
 * @param {string} requiredPermission - one of "dashboard"|"quotes"|"products"|"clients"|"vessels"|"tenders"
 *   Admin always passes. Staff passes only if their permissions list includes this key.
 *   Clients are denied unless `clientAllowed` is true.
 * @param {boolean} adminOnly - bypass permission check; only admins allowed.
 * @param {boolean} clientAllowed - permit clients through (default false).
 */
export default function ProtectedRoute({
  children,
  adminOnly = false,
  requiredPermission = null,
  clientAllowed = false,
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#F6F7F8]">
        <div className="text-[#5B6B7C] font-display tracking-widest text-sm">LOADING…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const role = user.role;
  // Clients are funneled to / (their dashboard); everything else redirects there.
  if (role === "client") {
    if (clientAllowed) return children;
    return <Navigate to="/" replace />;
  }
  if (adminOnly && role !== "admin") {
    return <Navigate to="/" replace />;
  }
  if (requiredPermission && role !== "admin") {
    const perms = user.permissions || [];
    if (!perms.includes(requiredPermission)) {
      // Send them to the first page they DO have access to (or /vessels as a safe default).
      const first = perms[0];
      return <Navigate to={first ? `/${first === "dashboard" ? "" : first}` : "/vessels"} replace />;
    }
  }
  return children;
}
